import { useEffect, useRef, useState } from 'react';
import { Button, Tooltip, message, Drawer } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { ProfileLogo } from './ProfileLogo';

import {
  setSelectedTemplate,
  setTemplateLoading,
  setTemplateError,
  clearTemplateError,
} from '../../store/slices/templateSlice';
import TemplateSelectDropdown from '../Settings/Settings.TemplateDropdown';
import GenerateSummaryButton from './GenerateSummaryButton';
import { listSummaries, regenerateSummary } from '../../store/actions/summary';

const TemplateTableWrapper = ({
  record,
  encounterPhase,
  setEncounterStatus,
  schedulepage,
  restrictTemplates,
  selectedRecord,
  searchFilters,
  drawerOpen = false,
  templatePopupOpen = false,
  handleCloseDrawer,
  setTemplateCount,
}) => {
  const dispatch = useDispatch();
  const { selectedTemplate, error: templateError } = useSelector(
    (state) => state?.template
  );

  const { summaryList } = useSelector((state) => state?.summaryState);

  const [templateList, setTemplateList] = useState([]);

  const [currentTemplate, setCurrentTemplate] = useState(null);

  const calculateTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));

    if (diffDays === 0) {
      if (diffHours === 0) return 'Just now';
      if (diffHours === 1) return '1 hour ago';
      return `${diffHours} hours ago`;
    }
    if (diffDays === 1) return '1 day ago';
    return `${diffDays} days ago`;
  };

  // Main function to extract unique templates and transform to desired format
  const extractTemplateList = (summariesResponse) => {
    const templateMap = new Map();

    // Filter summaries that have template_id and template_name
    const summariesWithTemplates = summariesResponse.filter(
      (summary) =>
        summary?.template_id ||
        (summary?.template_overrides_id && summary?.template_name) ||
        summary?.template_overrides_name
    );

    // Create unique templates map
    summariesWithTemplates.forEach((summary) => {
      const templateId = summary.template_id || summary?.template_overrides_id;
      const templateName =
        summary.template_name || summary?.template_overrides_name;

      if (!templateMap.has(templateId)) {
        templateMap.set(templateId, {
          template_id: templateId,
          template_name: templateName,
          status: summary.status === 'ready' ? 'Completed' : 'In Progress',
          last_saved: calculateTimeAgo(
            summary.updated_at || summary.created_at
          ),
          created_at: summary.created_at,
          summary_data: summary, // Store the full summary data
        });
      }
    });

    // Convert to array and add sequential keys
    const templateArray = Array.from(templateMap.values());

    // Sort by creation date (newest first) and add keys
    const templateList = templateArray
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .map((template, index) => ({
        key: String(index + 1),
        template_id: template.template_id,
        template_name: template.template_name,
        status: template.status,
        last_saved: template.last_saved,
        summary_data: template.summary_data,
      }));

    return templateList;
  };

  useEffect(() => {
    if (summaryList && summaryList.length > 0) {
      const templates = extractTemplateList(summaryList);
      setTemplateList(templates);
      setTemplateCount(templates?.length);
      // Auto-select first template on initial load if none is selected
      // and if encounter_phase is 'after-visit'
      if (!selectedTemplate && templates.length > 0) {
        const firstTemplate = templates[0];

        // Check if the summary has encounter_phase === 'after-visit'
        const hasAfterVisitPhase =
          firstTemplate.summary_data?.encounter_phase === 'after-visit';

        if (hasAfterVisitPhase) {
          const templateWithSummary = {
            template_id: firstTemplate.template_id,
            template_name: firstTemplate.template_name,
            status: firstTemplate.status,
            last_saved: firstTemplate.last_saved,
            summary_data: firstTemplate.summary_data,
            selected_at: new Date().toISOString(),
          };

          dispatch(setSelectedTemplate(templateWithSummary));
        }
      }
    }
  }, [summaryList, selectedTemplate, dispatch, encounterPhase]);

  const handleCardClick = (template) => {
    try {
      dispatch(clearTemplateError());
      dispatch(setTemplateLoading(true));

      // Find the complete summary data for this template
      const templateWithSummary = {
        template_id: template.template_id,
        template_name: template.template_name,
        status: template.status,
        last_saved: template.last_saved,
        summary_data: template.summary_data,
        selected_at: new Date().toISOString(),
      };

      // Dispatch to Redux store - this will also add to history
      dispatch(setSelectedTemplate(templateWithSummary));

      // Set encounter status
      setEncounterStatus('completed');

      // Call the external callback if provided
      if (setCurrentTemplate) {
        setCurrentTemplate(templateWithSummary);
      }

      message.success(`Template "${template.template_name}" selected`);
    } catch (error) {
      console.error('Error selecting template:', error);
      dispatch(setTemplateError('Failed to select template'));
      message.error('Failed to select template');
    } finally {
      dispatch(setTemplateLoading(false));
      handleCloseDrawer();
    }
  };

  // Format template status with appropriate colors
  const getStatusColor = (status) => {
    if (status === 'Completed') {
      return '#00d091'; // Green
    } else if (status === 'In Progress') {
      return '#ff9800'; // Orange
    } else {
      return '#9e9e9e'; // Gray
    }
  };

  // Template card component
  const TemplateCard = ({ template }) => {
    const isSelected = selectedTemplate?.template_id === template.template_id;
    const statusColor = getStatusColor(template.status);

    return (
      <div
        className={`border rounded-lg p-4 mb-4 cursor-pointer flex items-center transition-all ${
          isSelected
            ? 'border-[#00d091] bg-[#f0fdf9] shadow-md'
            : 'border-gray-200 hover:border-[#00d091] hover:bg-[#f8fdfc]'
        }`}
        onClick={() => handleCardClick(template)}
      >
        <ProfileLogo
          name={template.template_name}
          className={'profile-popover-logo mr-3'}
          practitioner_name={template.template_name}
          bgColor={statusColor}
        />

        <div className="flex-grow">
          <Tooltip
            title={
              <span
                onClick={(e) => {
                  navigator.clipboard.writeText(template?.template_id);
                  e.stopPropagation();
                  message.success('Template ID copied!', 1);
                }}
                className="cursor-pointer"
              >
                Template ID: {template?.template_id}
              </span>
            }
            placement="topLeft"
          >
            <div className="font-medium text-[14px] flex items-center gap-2">
              {template.template_name}
              {isSelected && (
                <span className="text-xs bg-[#00d091] text-white px-2 py-1 rounded">
                  Selected
                </span>
              )}
            </div>
          </Tooltip>

          <div className="flex items-center mt-1 text-[12px]">
            <span style={{ color: statusColor }}>{template.status}</span>
            <span className="text-gray-400 mx-2">•</span>
            <span className="text-gray-500">
              Last saved {template.last_saved}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);
  const lastEncounterId = useRef(record?.encounter_id);

  const updateSummariesAfterMagicEdit = (encounterId) => {
    setTimeout(async () => {
      try {
        if (lastEncounterId.current !== encounterId || !isMounted.current) {
          return; // Skip refresh if the user switched encounters
        }
        dispatch(listSummaries(encounterId, encounterPhase, 'ambient'));
        dispatch(listSummaries(encounterId, encounterPhase, 'icd-10'));
        dispatch(listSummaries(encounterId, encounterPhase, 'dictate'));
        dispatch(listSummaries(encounterId, encounterPhase, 'cpt'));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, 30000); // Refreshing after 30 seconds delay so that new summaries has been regenerated
  };

  const docuementRegeneration = () => {
    const currentEncounterId = record?.encounter_id;
    dispatch(
      regenerateSummary(
        record?.encounter_id,
        encounterPhase,
        searchFilters,
        '',
        currentTemplate,
        'comprehensive',
        'bulleted',
        'update'
      )
    ).then((success) => {
      if (success) {
        updateSummariesAfterMagicEdit(currentEncounterId);
        // isMagicEditingRef.current = false;
      }
    });
  };
  return (
    <>
      {/* Drawer containing the template list and popup */}
      <Drawer
        title={
          <div className="flex items-center justify-between">
            {templatePopupOpen ? (
              <div className="flex items-center gap-3">
                <span>Generate Document</span>
              </div>
            ) : (
              <span>Select summary template(s)</span>
            )}
          </div>
        }
        placement="right"
        width={400}
        onClose={handleCloseDrawer}
        open={drawerOpen}
        footer={
          !templatePopupOpen && (
            <div className="flex justify-end gap-2">
              <Button onClick={handleCloseDrawer}>Cancel</Button>
            </div>
          )
        }
      >
        <div className="flex flex-col h-full">
          {templateError && (
            <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {templateError}
            </div>
          )}

          {/* Template List View */}
          {!templatePopupOpen && (
            <div className="flex-grow overflow-auto">
              {templateList.length > 0 ? (
                templateList.map((template) => (
                  <TemplateCard
                    key={template.template_id || template.key}
                    template={template}
                  />
                ))
              ) : (
                <div className="text-center text-gray-500 mt-8">
                  <p>No templates found</p>
                </div>
              )}
            </div>
          )}

          {/* Template Selection Popup View */}
          {templatePopupOpen && (
            <div className="flex-grow flex flex-col p-4">
              <div className="mb-4">
                <label className="text-[#667085] text-sm font-medium mb-2 block">
                  Select document template
                </label>
                <TemplateSelectDropdown
                  encounterPhase={encounterPhase}
                  allowInlineEdit={true}
                  showToast={false}
                  currentTemplate={currentTemplate}
                  setCurrentTemplate={setCurrentTemplate}
                  fromAuraPage={!schedulepage}
                  restrictTemplates={restrictTemplates}
                />
              </div>

              <div className="flex justify-center mt-auto">
                <GenerateSummaryButton
                  encounter={selectedRecord}
                  encounterPhase={encounterPhase}
                  searchFilters={searchFilters}
                  currentTemplate={currentTemplate}
                  handleCompleteRecording={docuementRegeneration}
                />
              </div>
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default TemplateTableWrapper;
